<template>
  <div
    class="geofence-list-item"
    :class="{ disabled: !isActive, active: isSelected }">
    <div class="icon">
      <span
        v-show="!geofence.isPoint"
        class="badge badge-success no-text">
        <icon-polygon />
      </span>
      <span
        v-show="geofence.isPoint"
        class="badge badge-info no-text">
        <icon-pin />
      </span>
    </div>
    <div class="text">
      <strong :title="geofence.name">{{ geofence.name }}</strong>
      <span
        v-if="geofence.description != null && `${geofence.description}`.trim() != ''"
        class="address"
        :class="{ small: isEditable }"
        :title="geofence.description"
        >{{ geofence.description }}</span
      >
    </div>
    <div
      v-if="isEditable"
      class="actions">
      <button
        v-tooltip="{
          content: isActive ? capitalize($t('actions.delete')) : capitalize($t('actions.restore')),
          delay: 500,
        }"
        class="btn btn-xs b btn-no-text"
        :class="{ 'btn-danger': isActive }"
        @click.stop.prevent="toggleGeofence">
        <icon-delete v-if="isActive" />
        <icon-restore v-else />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize } from 'vue';

  import { IconDelete, IconPin, IconPolygon, IconRestore } from '@/common/icons/monochrome';
  import Geofence from '@/common/models/geo/Geofence';

  interface IGeofenceListProps {
    geofence: Geofence;
    isSelected: boolean;
    isActive: boolean;
    isEditable: boolean;
  }

  const props = defineProps<IGeofenceListProps>();

  interface IGeofenceListEmits {
    (e: 'toggleGeofence', geofence: Geofence): void;
  }

  const emit = defineEmits<IGeofenceListEmits>();

  function toggleGeofence() {
    emit('toggleGeofence', props.geofence);
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .geofence-list-item {
    display: flex;
    align-items: center;
    padding: 0.8rem 1rem;
    height: 4.6rem;

    .checkbox {
      margin-right: 0.6rem;
      padding: 0;
    }

    .icon {
      position: relative;
      flex-basis: 3.6rem;
      flex-shrink: 0;

      svg {
        width: 1.2rem;
        stroke: #fff;
        fill: #fff;
      }
    }

    .text {
      flex-grow: 1;
      padding-right: 0.8rem;
      display: flex;
      flex-direction: column;

      strong {
        display: inline-block;
        max-width: 21rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.5rem;
      }
    }

    .actions {
      flex-basis: 3.2rem;
      flex-shrink: 0;
    }

    .multiselect {
      display: inline-block;
      max-width: 24rem;
      margin-left: -0.6rem;
      margin-top: 1rem;

      .multiselect__tags {
        border-color: var(--actionsColor);

        width: 3rem;
        min-height: 2.4rem;
        padding: 0 3rem 0 0.7rem;
        border-radius: 0;
        position: relative;
        top: -0.4rem;
        cursor: pointer;

        .multiselect__single {
          color: var(--actionsColor);

          background: transparent;
          font-weight: 900;
        }

        &:hover {
          background: var(--actionsColor);
          color: var(--actionsColorReverse);

          .multiselect__single {
            color: var(--actionsColorReverse);
          }
        }
      }

      .multiselect__select {
        display: none;
      }

      .multiselect__content-wrapper {
        max-height: 30rem;
        width: 20rem;
        position: fixed;

        .multiselect__content {
          box-shadow: var(--panesShadow);

          max-height: initial !important;
          border-color: $borders;
          width: auto;
          font-weight: 400;
          border-radius: 0;
          border: 0.1rem solid $borders;

          .multiselect__element {
            font-size: 0.9em;

            span {
              margin: 0;
              padding: 0.8rem 1.2rem;
              min-height: 0;
            }
          }

          .multiselect__option {
            min-width: 10rem;
            border-top: 0.1rem solid $borders-light;
            cursor: pointer;

            svg {
              stroke: var(--textColor);

              width: 1.6rem;
              height: 1.6rem;
              display: inline-block;
              margin-right: 0.5rem;
            }

            .option__desc {
              display: inline-block;
              padding-right: 0.3rem;
              position: relative;
              top: -0.2rem;
            }
          }

          .multiselect__option--highlight {
            svg {
              stroke: #fff;
            }
          }
        }
      }
    }

    &.disabled {
      background-color: var(--paneHeaderBackgroundColorDark);
    }

    &.active {
      background: var(--primaryColor);
      color: var(--primaryColorReverse);
    }

    .address {
      font-size: 0.8em;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      display: block;
      max-width: 20.6rem;

      &.small {
        max-width: 20rem;
      }
    }
  }
</style>
